/**
 * Gift with Subscription
 *
 * Automatically adds a free gift product to the cart when any subscription
 * item is present, and removes it when no subscriptions remain.
 * The gift is added only once regardless of how many subscriptions are in cart.
 * The gift product cannot be purchased on its own.
 *
 * IMPORTANT: This script must be loaded BEFORE theme.js so that the fetch
 * intercept is in place before theme.AjaxProduct binds its submit handler.
 */
(function () {
  var GIFT_VARIANT_ID = 47071844073730;

  // ── Helpers (use raw _originalFetch to avoid re-entry) ──────────────

  var _originalFetch = window.fetch;

  function cartFetch(url, opts) {
    return _originalFetch.call(window, url, Object.assign({
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    }, opts));
  }

  function getCart() {
    return cartFetch('/cart.js?t=' + Date.now(), { method: 'GET' })
      .then(function (r) { return r.json(); });
  }

  function addGift() {
    return cartFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [{
          id: GIFT_VARIANT_ID,
          quantity: 1,
          properties: { '_gift': 'true' }
        }]
      })
    }).then(function (r) { return r.json(); });
  }

  function changeItem(key, qty) {
    return cartFetch('/cart/change.js?t=' + Date.now(), {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); });
  }

  function hasSubscriptionItem(cart) {
    return cart.items.some(function (item) {
      return item.selling_plan_allocation != null;
    });
  }

  function findGiftItem(cart) {
    return cart.items.find(function (item) {
      return item.variant_id === GIFT_VARIANT_ID;
    }) || null;
  }

  // ── Sync logic (returns a promise) ──────────────────────────────────

  function syncGift() {
    return getCart().then(function (cart) {
      var hasSub = hasSubscriptionItem(cart);
      var gift = findGiftItem(cart);

      if (hasSub && !gift) {
        return addGift();
      }
      if (!hasSub && gift) {
        return changeItem(gift.key, 0);
      }
      if (gift && gift.quantity > 1) {
        return changeItem(gift.key, 1);
      }
    }).catch(function (err) {
      console.error('[Gift w/ Subscription]', err);
    });
  }

  // ── Intercept fetch: add gift BEFORE the response reaches the theme ─
  //
  // When theme.AjaxProduct posts to /cart/add.js the flow is:
  //   fetch('/cart/add.js') → .then(response.json()) → dispatches ajaxProduct:added
  //   → CartDrawer hears event → calls buildCart() → fetches cart page markup
  //
  // We intercept /cart/add.js so that *before* the response is returned
  // to the theme code, we check if a subscription was just added and
  // silently inject the gift. By the time the theme fires its event and
  // the drawer calls buildCart(), the gift is already in the cart.
  //
  // For /cart/change.js and /cart/update.js we do the same — the theme's
  // cart:updated event and subsequent buildCart() will already reflect
  // the gift being added or removed.

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';

    // ── /cart/add.js intercept ────────────────────────────────────────
    if (url.indexOf('/cart/add') !== -1) {

      // Block direct add-to-cart of the gift variant
      var body = (init && init.body) ? init.body : null;
      if (body && isGiftOnlyAdd(body)) {
        return Promise.resolve(new Response(JSON.stringify({ status: 422, message: 'This item cannot be added directly.' }), {
          status: 422,
          headers: { 'Content-Type': 'application/json' }
        }));
      }

      // Let the real add happen, then sync gift before returning response
      return _originalFetch.apply(this, arguments).then(function (response) {
        // Clone so we can read the body without consuming it
        var clone = response.clone();
        return clone.json()
          .then(function (data) {
            if (data.status === 422) return response; // add failed, nothing to do
            // Check whether the item just added is a subscription
            var items = data.items || [data]; // /cart/add returns { items: [...] } or single item
            var justAddedSub = items.some(function (item) {
              return item.selling_plan_allocation != null;
            });
            if (justAddedSub) {
              // Check current cart for existing gift
              return getCart().then(function (cart) {
                var gift = findGiftItem(cart);
                if (!gift) {
                  return addGift().then(function () { return response; });
                }
                return response;
              });
            }
            return response;
          })
          .catch(function () { return response; }); // safety net
      });
    }

    // ── /cart/change.js and /cart/update.js intercept ─────────────────
    if (url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1) {
      return _originalFetch.apply(this, arguments).then(function (response) {
        var clone = response.clone();
        return clone.json()
          .then(function (cart) {
            if (!cart.items) return response; // not a cart object
            var hasSub = hasSubscriptionItem(cart);
            var gift = findGiftItem(cart);

            if (!hasSub && gift) {
              // Subscription was removed — remove the gift
              return changeItem(gift.key, 0).then(function (updatedCart) {
                // Return a new response with the updated cart so the theme
                // renders the correct state (without the gift)
                return new Response(JSON.stringify(updatedCart), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' }
                });
              });
            }
            if (hasSub && !gift) {
              // Edge case: subscription added via /cart/update
              return addGift().then(function () {
                return getCart().then(function (freshCart) {
                  return new Response(JSON.stringify(freshCart), {
                    status: 200,
                    headers: { 'Content-Type': 'application/json' }
                  });
                });
              });
            }
            if (gift && gift.quantity > 1) {
              return changeItem(gift.key, 1).then(function (updatedCart) {
                return new Response(JSON.stringify(updatedCart), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' }
                });
              });
            }
            return response;
          })
          .catch(function () { return response; });
      });
    }

    // ── Everything else: pass through ────────────────────────────────
    return _originalFetch.apply(this, arguments);
  };

  // ── Detect if a /cart/add body is ONLY adding the gift variant ──────

  function isGiftOnlyAdd(body) {
    try {
      var parsed;
      if (typeof body === 'string') {
        parsed = JSON.parse(body);
      } else if (body instanceof FormData) {
        var id = body.get('id');
        return id && Number(id) === GIFT_VARIANT_ID;
      } else {
        return false;
      }

      // Single item format: { id: ..., quantity: ... }
      if (parsed.id && Number(parsed.id) === GIFT_VARIANT_ID) {
        return true;
      }
      // Multi-item format: { items: [{ id: ... }] }
      if (parsed.items && parsed.items.length === 1 && Number(parsed.items[0].id) === GIFT_VARIANT_ID) {
        return true;
      }
    } catch (e) {}
    return false;
  }

  // ── Prevent standalone purchase via form / button on the page ───────

  function blockGiftForms() {
    document.querySelectorAll(
      'form[action*="/cart/add"] input[name="id"][value="' + GIFT_VARIANT_ID + '"]'
    ).forEach(function (input) {
      var form = input.closest('form');
      if (form && !form.dataset.giftBlocked) {
        form.dataset.giftBlocked = 'true';
        var btn = form.querySelector('[type="submit"], .add-to-cart');
        if (btn) {
          btn.disabled = true;
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.5';
          btn.setAttribute('title', 'This item is a free gift with subscription');
        }
        form.addEventListener('submit', function (e) { e.preventDefault(); });
      }
    });
  }

  // ── Initial page load sync + form blocking ─────────────────────────

  function init() {
    syncGift().then(function () {
      // Rebuild cart UI after sync on page load
      document.dispatchEvent(new CustomEvent('cart:build'));
    });
    blockGiftForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run form blocking after Shopify section re-renders (theme editor)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', blockGiftForms);
  }
})();